import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MouseIcon from '@mui/icons-material/Mouse';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LinkIcon from '@mui/icons-material/Link';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { Select } from 'root/src/client/v2/common/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { usePointsTransactions, getTransactionLabel, isPayoutTransaction, TRANSACTION_SECTION_OPTIONS, DEFAULT_TRANSACTION_SECTION, } from './usePointsTransactions';
const PAGE_SIZE = 5;
const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatWatchTime = (minutes) => {
    const hours = Math.round((minutes / 60) * 100) / 100;
    return `${hours} hrs`;
};
const TransactionRow = ({ row, index, animate }) => {
    var _a, _b, _c, _d;
    const label = getTransactionLabel(row.type);
    const payout = isPayoutTransaction(row.type);
    const subtitle = (_a = row.campaignName) !== null && _a !== void 0 ? _a : row.description;
    const date = formatDate(row.created);
    const showClicks = typeof row.clicks === 'number' && row.clicks > 0;
    const showWatchTime = typeof row.watchTimeMinutes === 'number' && row.watchTimeMinutes > 0;
    // Initial product is the (decrypted) reward; the handler emits 'not received' when there is none.
    const showProduct = Boolean(row.initialProduct) && row.initialProduct !== 'not received';
    const showCtaUrl = Boolean(row.landingUrl);
    return (<motion.div initial={animate ? { opacity: 0, y: 12, scale: 0.98 } : false} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.25, ease: 'easeOut', delay: animate ? index * 0.05 : 0 }} className="flex items-center justify-between gap-3 rounded-xl border border-solid border-border/50 bg-secondary/30 p-3">
			<div className="min-w-0 space-y-1">
				<div className="flex items-center gap-2">
					<span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', payout ? 'bg-neon-pink' : 'bg-neon-green')}/>
					<span className="truncate text-xs font-semibold text-foreground">{label}</span>
				</div>
				{subtitle && (<Tooltip>
						<TooltipTrigger asChild>
							<p className="cursor-default truncate text-[11px] text-muted-foreground">{subtitle}</p>
						</TooltipTrigger>
						<TooltipContent className="max-w-[260px] break-words">{subtitle}</TooltipContent>
					</Tooltip>)}
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground/70">
					{date && <span>{date}</span>}
					{showClicks && (<span className="inline-flex items-center gap-1">
							<MouseIcon sx={{ fontSize: 12 }}/>
							{formatNumberWithCommas((_b = row.clicks) !== null && _b !== void 0 ? _b : 0)} clicks
						</span>)}
					{showWatchTime && (<span className="inline-flex items-center gap-1">
							<ScheduleIcon sx={{ fontSize: 12 }}/>
							{formatWatchTime((_c = row.watchTimeMinutes) !== null && _c !== void 0 ? _c : 0)}
						</span>)}
					{showProduct && (<Tooltip>
							<TooltipTrigger asChild>
								<span className="inline-flex max-w-[140px] cursor-default items-center gap-1">
									<VpnKeyIcon sx={{ fontSize: 12 }} className="shrink-0"/>
									<span className="truncate">{row.initialProduct}</span>
								</span>
							</TooltipTrigger>
							<TooltipContent className="max-w-[260px] break-all">{row.initialProduct}</TooltipContent>
						</Tooltip>)}
					{showCtaUrl && (<Tooltip>
							<TooltipTrigger asChild>
								<a href={row.landingUrl} target="_blank" rel="noreferrer" className="inline-flex max-w-[150px] items-center gap-1 text-accent transition-colors hover:text-accent/80">
									<LinkIcon sx={{ fontSize: 12 }} className="shrink-0"/>
									<span className="truncate">{row.landingUrl}</span>
								</a>
							</TooltipTrigger>
							<TooltipContent className="max-w-[260px] break-all">{row.landingUrl}</TooltipContent>
						</Tooltip>)}
				</div>
			</div>
			<div className="shrink-0 text-right">
				<span className={cn('font-display text-sm font-semibold', payout ? 'text-neon-pink' : 'text-neon-green')}>
					{formatNumberWithCommas((_d = row.points) !== null && _d !== void 0 ? _d : 0)}
				</span>
				<span className="block text-[10px] text-muted-foreground">pts</span>
			</div>
		</motion.div>);
};
// Transaction history list for the redesign Payments tab. Ports the legacy creatorActivityDetailsList
// (GET_CREATOR_POINTS_TRANSACTIONS) with the same time-range filter; paging is client-side because the
// endpoint returns the full set for a section. Loading, error and empty states reserve height (no CLS)
// and the list cross-fades between filter/page changes with a staggered row enter.
export const TransactionHistoryList = () => {
    const reduceMotion = useReducedMotion();
    const [section, setSection] = useState(DEFAULT_TRANSACTION_SECTION);
    const [page, setPage] = useState(1);
    const { transactions, isLoading, isError } = usePointsTransactions(Number(section));
    const totalPages = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
    // Reset to the first page when the filter changes, and clamp if the set shrinks after a refetch.
    useEffect(() => setPage(1), [section]);
    useEffect(() => {
        setPage(prev => Math.min(prev, totalPages));
    }, [totalPages]);
    const pageRows = useMemo(() => transactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [transactions, page]);
    const animateRows = !reduceMotion;
    return (<TooltipProvider delayDuration={200}>
			<div className="space-y-3">
				<div className="flex items-center justify-end">
					<Select value={section} onValueChange={setSection} options={TRANSACTION_SECTION_OPTIONS} className="h-8 w-[140px] text-xs"/>
				</div>

				<div className="min-h-[240px]">
					<AnimatePresence mode="wait" initial={false}>
						{isLoading ? (<motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="space-y-2">
								{Array.from({ length: 4 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="flex items-center justify-between gap-3 rounded-xl border border-solid border-border/50 bg-secondary/30 p-3">
										<div className="min-w-0 flex-1 space-y-2">
											<Skeleton className="h-3 w-32"/>
											<Skeleton className="h-2.5 w-44 max-w-full"/>
										</div>
										<Skeleton className="h-4 w-12 shrink-0"/>
									</div>))}
							</motion.div>) : isError ? (<motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-destructive/30 p-6 text-center">
								<p className="text-xs font-semibold text-foreground">Could not load your transactions</p>
								<p className="mt-1 text-[11px] text-muted-foreground">Please try again in a moment.</p>
							</motion.div>) : transactions.length === 0 ? (<motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-border/50 p-6 text-center">
								<ReceiptLongIcon sx={{ fontSize: 32 }} className="mb-2 text-muted-foreground/30"/>
								<p className="text-xs text-muted-foreground">No transactions yet</p>
							</motion.div>) : (<motion.div key={`list-${section}-${page}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="space-y-2">
								{pageRows.map((row, index) => (<TransactionRow key={row.id} row={row} index={index} animate={animateRows}/>))}
							</motion.div>)}
					</AnimatePresence>
				</div>

				{!isLoading && !isError && totalPages > 1 && (<div className="flex items-center justify-between pt-1">
						<span className="text-[11px] text-muted-foreground">Page {page} of {totalPages}</span>
						<div className="flex items-center gap-1">
							<Button size="sm" variant="outline" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page <= 1} aria-label="Previous page" className="h-7 w-7 rounded-lg p-0">
								<ChevronLeftIcon sx={{ fontSize: 18 }}/>
							</Button>
							<Button size="sm" variant="outline" onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page >= totalPages} aria-label="Next page" className="h-7 w-7 rounded-lg p-0">
								<ChevronRightIcon sx={{ fontSize: 18 }}/>
							</Button>
						</div>
					</div>)}
			</div>
		</TooltipProvider>);
};
